import React from "react";
import PropTypes from "prop-types";
//Material imports
import makeStyles from "@mui/styles/makeStyles";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";

// Limitless imports
import ModernTaskListHeader from "./ModernTaskListHeader";
import { useExpertMessageContext } from "@limitless/expert/hooks/expert-message/ExpertMessageContext";
import { TASK_TYPE_LABEL } from "@limitless/expert/hooks/expert-task-list/TaskListHelper";
import { LinearProgress } from "@limitless/portal";
import { useExpertProfileContext, useMessagePass, useExpertTaskListContext } from "@limitless/expert";
//Modern imports
import ModernTaskListAvatar from "../modern-task-avatar/ModernTasksAvatar";
import Grid from "@limitless/modern/layout/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Chip from "@limitless/modern/data-display/chip/Chip";
import Button from "@limitless/modern/inputs/button/Button";
import ModernTaskListItemLock from "./ModernTaskListItemLock";
import ModernTaskListPreview from "../modern-task-list-preview/ModernTaskListPreview";

const useStyles = makeStyles(theme => ({
    contentWrapper: {
        display: "flex",
        flexDirection: "column",
        paddingLeft: 8,
        width: "100%"
    },
    messageWrapper: {
        display: "flex",
        width: "100%",
        overflow: "hidden",
        wordWrap: "break-word",
        wordBreak: "break-word",
        marginTop: 4,
        whiteSpace: "normal",
        height: 50
    },
    taskBody: {
        lineHeight: "24px"
    },
    itemContainer: {
        transition: "opacity 100ms ease-out, height 150ms ease-out 200ms",
        height: ({ fadeout }) => (fadeout ? "0px" : "150px"),
        opacity: ({ fadeout }) => (fadeout ? 0.01 : 1)
    },
    listItemContainer: {
        height: 150,
        padding: "16px 0px!important",
        paddingLeft: "4px !important",
        paddingRight: "16px !important",
        alignItems: "flex-start"
    },
    listItemContainerSticky: {
        background: theme.palette.sticky.light
    }
}));

const renderButtons = (showPassButton, tabId, handlePassedMessage, handleClaimMessage, hasPressed, message) => {
    return (
        <>
            <Grid item>
                {showPassButton && tabId === 0 && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handlePassedMessage}
                        disabled={message.claimedByAnother}
                    >
                        Pass
                    </Button>
                )}
            </Grid>
            <Grid item>
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleClaimMessage}
                    disabled={message.claimedByAnother || hasPressed}
                >
                    Claim
                </Button>
            </Grid>
        </>
    );
};

const ModernTaskListItem = ({
    message,
    onRemoveTask,
    claimSelectedMessage,
    onSelectMessage,
    contentVisible = true,
    loading,
    tabId,
    onUnselectMessage,
    selectedMessageId,
    showPassButton = false,
    handlePassMessage,
    setHasClaim = () => null,
    onTaskListRefresh = () => null
}) => {
    const [fadeout, setFadeout] = React.useState(false);
    const [hasPressed, setHasPressed] = React.useState(false);
    const [shouldFadeout, setShouldFadeout] = React.useState(false);
    const [hasPressedPassButton, setHasPressedPassButton] = React.useState();

    const [isPreview, setIsPreview] = React.useState(false);
    const { profile } = useExpertProfileContext();
    const classes = useStyles({ fadeout });
    const { passMessage, unPassMessage } = useMessagePass();
    const { onClaimMessage, claimMessage, clearMessage } = useExpertMessageContext();
    const { tasklist } = useExpertTaskListContext();

    React.useEffect(() => {
        if (message.claimedByAnother) {
            setFadeout(false);
        }
    }, [message.claimedByAnother]);

    React.useEffect(() => {
        setHasPressed(false);
        setHasClaim(false);
        setHasPressedPassButton(false);
    }, [message.conversationState?.state]);

    const filteredMessages = React.useMemo(() => ({ ...tasklist.filteredMessages }), [tasklist.filteredMessages]);

    React.useEffect(() => {
        const timer = setTimeout(
            () => {
                setShouldFadeout(!!filteredMessages[message?.messageId]?.claimedByAnother);
            },
            hasPressedPassButton ? 0 : 3000
        );

        return () => clearTimeout(timer);
    }, [filteredMessages, message?.messageId, hasPressedPassButton]);

    React.useEffect(() => {
        if (shouldFadeout) {
            setFadeout(true);
        }
    }, [shouldFadeout]);

    const fadeoutItemHandler = () => {
        if (shouldFadeout) {
            setFadeout(true);
        }
    };

    const onMessageClaim = () => {
        if (selectedMessageId === "") {
            claimSelectedMessage(message, (messageResources, profile, messageInbox) => {
                onClaimMessage(messageInbox, messageResources, profile, data => {
                    if (data.hasError) {
                        setHasClaim(false);
                    }
                });
            });
        } else {
            claimMessage();
        }
    };

    const handleClaimMessage = () => {
        if (!hasPressed) {
            setHasPressed(true);
            setHasClaim(true);
            if (tabId === 1) {
                unPassMessage(message, res => {
                    onMessageClaim();
                });
            } else {
                onMessageClaim();
            }
        }
    };

    const handlePassedMessage = () => {
        setHasPressed(true);
        setHasPressedPassButton(true);
        passMessage(true, message);
        clearMessage();
    };

    const removeTask = event => {
        if (fadeout && onRemoveTask && event.propertyName === "opacity") {
            onRemoveTask(message.messageId);
        }
    };

    const handlePreviewClick = () => {
        setIsPreview(true);
        onSelectMessage(message);
    };

    const handleClosePreview = () => {
        onUnselectMessage();
        setIsPreview(false);
        if (selectedMessageId !== "" && message.claimedByAnother) {
            onTaskListRefresh();
        }
    };

    const feeLabel = React.useMemo(() => {
        let label;
        if (message.rewardFee > 0 && message.points > 0) {
            label = `${profile.balanceCurrencySymbol}${Number(message.rewardFee).toFixed(2)} / ${message.points} pts`;
        } else if (message.rewardFee > 0) {
            label = `${profile.balanceCurrencySymbol}${Number(message.rewardFee).toFixed(2)} `;
        } else if (message.points > 0) {
            label = `${message.points} pts`;
        }

        return label;
    }, [message.rewardFee, message.points]);

    console.log("Test Log");

    const taskLockIndicator = React.useMemo(() => {
        return message.claimedByAnother && !hasPressedPassButton ? (
            <ModernTaskListItemLock onTransitionComplete={fadeoutItemHandler} tasklist={tasklist} />
        ) : null;
    }, [message.claimedByAnother, tasklist, fadeoutItemHandler]);

    return (
        <>
            {contentVisible ? (
                <>
                    <div onTransitionEnd={removeTask} className={classes.itemContainer}>
                        <ListItem
                            onClick={e => {
                                if (e.detail === 2) {
                                    !message.claimedByAnother && handleClaimMessage();
                                }
                            }}
                            className={[
                                classes.listItemContainer,
                                message.type === "respondSticky" && classes.listItemContainerSticky
                            ]}
                            sx={{
                                "&.MuiListItem-root:hover ": {
                                    cursor: "default"
                                }
                            }}
                        >
                            <ModernTaskListAvatar type={message.type} isPassed={message.passed} isTooltipShow />
                            <div className={classes.contentWrapper}>
                                <ModernTaskListHeader message={message} />
                                <div className={classes.messageWrapper}>
                                    <Typography className={classes.taskBody} variant="body2" color="textPrimary">
                                        {message?.body}
                                        {message.body?.length === 170 && "..."}
                                    </Typography>
                                </div>
                                <Grid
                                    container
                                    mt={0.5}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item xs={2} md={3} lg={6} container direction="row" alignItems="center">
                                        {feeLabel && (
                                            <Chip
                                                sx={{
                                                    backgroundColor: theme => theme.palette.secondary.light,
                                                    minWidth: 57
                                                }}
                                                label={feeLabel}
                                            />
                                        )}
                                        <Grid ml={feeLabel ? "5px" : 0}>
                                            <Button
                                                size="small"
                                                disabled={hasPressed}
                                                onClick={handlePreviewClick}
                                                style={feeLabel ? {} : { padding: "10px" }}
                                                classes={!feeLabel && classes.buttonPadding}
                                            >
                                                Preview
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={10}
                                        md={9}
                                        lg={6}
                                        container
                                        spacing={1}
                                        justifyContent="flex-end"
                                        alignItems="center"
                                    >
                                        {renderButtons(
                                            showPassButton,
                                            tabId,
                                            handlePassedMessage,
                                            handleClaimMessage,
                                            hasPressed,
                                            message
                                        )}
                                    </Grid>
                                </Grid>
                            </div>
                            {loading && !isPreview && <LinearProgress />}
                            {taskLockIndicator}
                        </ListItem>
                        <Divider sx={{ marginLeft: 2, marginRight: 2 }} />
                    </div>
                    {isPreview && (
                        <ModernTaskListPreview
                            tabId={tabId}
                            message={message}
                            onClose={handleClosePreview}
                            loading={loading}
                            fadeoutItemHandler={fadeoutItemHandler}
                            type={message.type}
                            points={message.points}
                            rewardFee={Number(message.rewardFee).toFixed(2)}
                            messageDetails={message}
                            feeLabel={feeLabel}
                            balanceCurrencySymbol={profile.balanceCurrencySymbol}
                        />
                    )}
                </>
            ) : (
                <Grid item xs={9} md={9} lg={9} container spacing={1} justifyContent="flex-end">
                    {renderButtons(showPassButton, tabId, handlePassedMessage, handleClaimMessage, hasPressed, message)}
                </Grid>
            )}
        </>
    );
};

export default ModernTaskListItem;

ModernTaskListItem.propTypes = {
    message: PropTypes.object.isRequired,
    onRemoveTask: PropTypes.func,
    onSelectMessage: PropTypes.func,
    claimSelectedMessage: PropTypes.func,
    loading: PropTypes.bool,
    contentVisible: PropTypes.bool,
    selectedMessageId: PropTypes.string,
    tabId: PropTypes.number,
    onUnselectMessage: PropTypes.func,
    setHasClaim: PropTypes.func,
    showPassButton: PropTypes.bool,
    handlePassMessage: PropTypes.func,
    onTaskListRefresh: PropTypes.func
};
